/**
 * Keyboard Navigation Utility
 *
 * A small, reusable "hook-like" helper to add:
 * - Enter: move focus to next field (prevents submit)
 * - Escape: restore current field to its initial value snapshot
 */

import { FormBase } from "../../util/form-base-form";
import { attachKeyboardNavigationKeydownListener } from "./keyboard-navigation-listener";

type EscapeMode = "field" | "form";
type SnapshotValue = string | boolean | string[];
type SnapshotStore = Record<string, SnapshotValue>;

interface KeyboardNavigationOptions {
    enableEnter?: boolean;
    enableEscape?: boolean;
    escapeMode?: EscapeMode;
    orderAttribute?: string;
    nextAttribute?: string;
    selectAllOnMove?: boolean;
    isEnterAllowedTarget?: (el: HTMLElement) => boolean;
    isEscapeAllowedTarget?: (el: HTMLElement) => boolean;
    isNextAllowedTarget?: (el: HTMLElement) => boolean;
}

interface KeyboardNavigationHandle {
    capture(): void;
    focusFirstText(): void;
    clear(): void;
    detach(): void;
}

class KeyboardNavigationFormBase extends FormBase {
    public getFocusableElements(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, "input, select, textarea, button", HTMLElement);
    }

    public getFormFields(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, "input, select, textarea", HTMLElement);
    }

    public getNamedElement(root: ParentNode, name: string): HTMLElement | null {
        return this.queryElement(root, `[name="${CSS.escape(name)}"]`, HTMLElement);
    }

    public getNamedElements(root: ParentNode, name: string): HTMLElement[] {
        return this.queryElements(root, `[name="${CSS.escape(name)}"]`, HTMLElement);
    }

    public getRadioInputs(root: ParentNode, name: string): HTMLInputElement[] {
        return this.queryElements(root, `input[type="radio"][name="${CSS.escape(name)}"]`, HTMLInputElement);
    }

    public getCheckboxInputs(root: ParentNode, name: string): HTMLInputElement[] {
        return this.queryElements(root, `input[type="checkbox"][name="${CSS.escape(name)}"]`, HTMLInputElement);
    }

    public getExplicitNext(root: ParentNode, explicit: string): HTMLElement | null {
        const looksLikeSelector = explicit.startsWith("#") || explicit.startsWith(".") || explicit.startsWith("[");
        if (looksLikeSelector) {
            return this.queryElement(root, explicit, HTMLElement);
        }
        return this.queryElement(root, `[name="${CSS.escape(explicit)}"]`, HTMLElement);
    }
}

const keyboardNavigationFormBase = new KeyboardNavigationFormBase();

function isHidden(el: HTMLElement): boolean {
    if (!el.isConnected) return true;
    if (el.offsetParent !== null) return false;
    return el.getClientRects().length === 0;
}

function isFocusable(el: HTMLElement): boolean {
    if (el.getAttribute("tabindex") === "-1") return false;
    if (isHidden(el)) return false;
    if (el.closest(".ql-toolbar")) return false;

    if (el instanceof HTMLInputElement) {
        if (el.type === "hidden") return false;
        if (el.disabled) return false;
        if (el.readOnly) return false;
        return true;
    }

    if (el instanceof HTMLTextAreaElement) {
        if (el.disabled) return false;
        if (el.readOnly) return false;
        return true;
    }

    if (el instanceof HTMLSelectElement) {
        if (el.disabled) return false;
        if (el.classList.contains("form-control--readonly")) return false;
        return true;
    }

    if (el instanceof HTMLButtonElement) {
        return !el.disabled;
    }

    return false;
}

function getOrderedFocusableElements(root: HTMLElement, orderAttribute: string): HTMLElement[] {
    const elements = keyboardNavigationFormBase
        .getFocusableElements(root)
        .map((el, idx) => {
            const attr = el.getAttribute(orderAttribute);
            const attrOrder = attr !== null && attr !== "" ? Number(attr) : null;
            const tabOrder = el.tabIndex > 0 ? el.tabIndex : null;
            return {
                el,
                idx,
                order: Number.isFinite(attrOrder) ? attrOrder : tabOrder,
            };
        })
        .filter((item) => isFocusable(item.el));

    const hasAnyOrder = elements.some((item) => item.order !== null);
    if (!hasAnyOrder) {
        return elements.sort((a, b) => a.idx - b.idx).map((item) => item.el);
    }

    return elements
        .sort((a, b) => {
            const ao = a.order;
            const bo = b.order;
            if (ao === null && bo === null) return a.idx - b.idx;
            if (ao === null) return 1;
            if (bo === null) return -1;
            if (ao !== bo) return ao - bo;
            return a.idx - b.idx;
        })
        .map((item) => item.el);
}

function selectAllText(el: HTMLElement): void {
    if (el instanceof HTMLTextAreaElement) {
        try {
            el.select();
        } catch (_error) {
            // ignore
        }
        return;
    }

    if (el instanceof HTMLInputElement) {
        const type = (el.type || "").toLowerCase();
        const selectableTypes = new Set([
            "text",
            "search",
            "url",
            "tel",
            "email",
            "password",
            "number",
            "date",
            "datetime-local",
            "time",
        ]);
        if (!selectableTypes.has(type)) return;
        try {
            el.select();
        } catch (_error) {
            // ignore
        }
    }
}

function focusNext(
    root: HTMLElement,
    current: HTMLElement,
    nextAttribute: string,
    orderAttribute: string,
    afterFocus: ((el: HTMLElement) => void) | undefined,
    isNextAllowedTarget: (el: HTMLElement) => boolean,
): void {
    const explicit = current.getAttribute(nextAttribute);
    if (explicit) {
        const next = keyboardNavigationFormBase.getExplicitNext(root, explicit);

        if (next instanceof HTMLElement && isFocusable(next) && isNextAllowedTarget(next)) {
            next.focus();
            if (typeof afterFocus === "function") afterFocus(next);
            return;
        }
    }

    const focusables = getOrderedFocusableElements(root, orderAttribute);
    const idx = focusables.indexOf(current);
    if (idx === -1) return;

    for (let i = idx + 1; i < focusables.length; i += 1) {
        const next = focusables[i];
        if (!isNextAllowedTarget(next)) continue;

        next.focus();
        if (typeof afterFocus === "function") afterFocus(next);
        return;
    }
}

function createSnapshotStore(): SnapshotStore {
    return {};
}

function captureSnapshot(root: HTMLElement): SnapshotStore {
    const snapshot = createSnapshotStore();

    const elements = keyboardNavigationFormBase
        .getFormFields(root)
        .filter(
            (el): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
                el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement,
        );

    elements.forEach((el) => {
        const name = el.name || "";
        if (!name) return;
        if (el.disabled) return;

        if (el instanceof HTMLInputElement) {
            if (el.type === "radio" || el.type === "checkbox") return;
            snapshot[name] = el.value;
            return;
        }

        if (el instanceof HTMLSelectElement) {
            snapshot[name] = el.multiple ? Array.from(el.selectedOptions).map((option) => option.value) : el.value;
            return;
        }

        snapshot[name] = el.value;
    });

    const radioNames = new Set(
        elements
            .filter((el): el is HTMLInputElement => el instanceof HTMLInputElement && el.type === "radio")
            .map((el) => el.name)
            .filter(Boolean),
    );
    radioNames.forEach((name) => {
        const radios = keyboardNavigationFormBase.getRadioInputs(root, name);
        const checked = radios.find((radio) => radio.checked);
        snapshot[name] = checked ? checked.value : "";
    });

    const checkboxNames = new Set(
        elements
            .filter((el): el is HTMLInputElement => el instanceof HTMLInputElement && el.type === "checkbox")
            .map((el) => el.name)
            .filter(Boolean),
    );
    checkboxNames.forEach((name) => {
        const boxes = keyboardNavigationFormBase.getCheckboxInputs(root, name);

        if (boxes.length <= 1) {
            const box = boxes[0];
            snapshot[name] = box instanceof HTMLInputElement ? !!box.checked : false;
            return;
        }

        snapshot[name] = boxes.filter((box) => box.checked).map((box) => box.value);
    });

    return snapshot;
}

function restoreField(root: HTMLElement, target: HTMLElement, snapshot: SnapshotStore): boolean {
    const name =
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
            ? target.name || ""
            : "";

    if (!name) return false;
    if (!Object.prototype.hasOwnProperty.call(snapshot, name)) return false;

    const saved = snapshot[name];

    if (target instanceof HTMLInputElement) {
        if (target.type === "radio") {
            const radios = keyboardNavigationFormBase.getRadioInputs(root, name);
            radios.forEach((radio) => {
                radio.checked = String(saved) !== "" && radio.value === String(saved);
            });
            return true;
        }

        if (target.type === "checkbox") {
            const boxes = keyboardNavigationFormBase.getCheckboxInputs(root, name);

            if (Array.isArray(saved)) {
                boxes.forEach((box) => {
                    box.checked = saved.includes(box.value);
                });
                return true;
            }

            boxes.forEach((box) => {
                box.checked = !!saved;
            });
            return true;
        }

        target.value = typeof saved === "string" ? saved : String(saved ?? "");
        return true;
    }

    if (target instanceof HTMLSelectElement) {
        if (target.multiple && Array.isArray(saved)) {
            const set = new Set(saved.map(String));
            Array.from(target.options).forEach((option) => {
                option.selected = set.has(option.value);
            });
            return true;
        }

        target.value = typeof saved === "string" ? saved : String(saved ?? "");
        return true;
    }

    if (target instanceof HTMLTextAreaElement) {
        target.value = typeof saved === "string" ? saved : String(saved ?? "");
        return true;
    }

    return false;
}

function restoreAll(root: HTMLElement, snapshot: SnapshotStore): boolean {
    const names = Object.keys(snapshot || {});
    if (!names.length) return false;

    let restoredAny = false;

    names.forEach((name) => {
        const first = keyboardNavigationFormBase.getNamedElement(root, name);
        if (!(first instanceof HTMLElement)) return;

        const restored = restoreField(root, first, snapshot);
        if (restored) {
            restoredAny = true;
            keyboardNavigationFormBase.getNamedElements(root, name).forEach((el) => {
                el.dispatchEvent(new Event("input", { bubbles: true }));
                el.dispatchEvent(new Event("change", { bubbles: true }));
            });
        }
    });

    return restoredAny;
}

export function attachEnterEscapeNavigation(
    root: HTMLElement,
    options: KeyboardNavigationOptions = {},
): KeyboardNavigationHandle {
    const enableEnter = options.enableEnter !== false;
    const enableEscape = options.enableEscape !== false;
    const escapeMode: EscapeMode = options.escapeMode === "form" ? "form" : "field";
    const orderAttribute = options.orderAttribute || "data-kbd-order";
    const nextAttribute = options.nextAttribute || "data-kbd-next";
    const selectAllOnMove = options.selectAllOnMove !== false;

    const isEnterAllowedTarget =
        options.isEnterAllowedTarget ||
        ((el: HTMLElement): boolean =>
            el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement);

    const isEscapeAllowedTarget =
        options.isEscapeAllowedTarget ||
        ((el: HTMLElement): boolean =>
            el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement);

    const isNextAllowedTarget = options.isNextAllowedTarget || (() => true);

    let snapshot = createSnapshotStore();

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.isComposing) return;

        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        if (enableEscape && event.key === "Escape" && isEscapeAllowedTarget(target)) {
            if (escapeMode === "form") {
                const restored = restoreAll(root, snapshot);
                if (restored) {
                    event.preventDefault();
                }
                return;
            }

            const restored = restoreField(root, target, snapshot);
            if (restored) {
                target.dispatchEvent(new Event("input", { bubbles: true }));
                target.dispatchEvent(new Event("change", { bubbles: true }));
                event.preventDefault();
            }
            return;
        }

        if (enableEnter && event.key === "Enter" && isEnterAllowedTarget(target)) {
            if (target instanceof HTMLTextAreaElement) return;
            if (target instanceof HTMLButtonElement) return;
            if (target instanceof HTMLInputElement && (target.type === "submit" || target.type === "button")) return;

            event.preventDefault();
            focusNext(
                root,
                target,
                nextAttribute,
                orderAttribute,
                (next) => {
                    if (!selectAllOnMove) return;
                    selectAllText(next);
                },
                isNextAllowedTarget,
            );
        }
    };

    const listenerHandle = attachKeyboardNavigationKeydownListener(root, onKeydown);

    return {
        capture(): void {
            snapshot = captureSnapshot(root);
        },
        focusFirstText(): void {
            const focusables = getOrderedFocusableElements(root, orderAttribute);
            const firstText = focusables.find((el) => {
                if (el instanceof HTMLTextAreaElement) return true;
                if (el instanceof HTMLInputElement) {
                    const type = (el.type || "").toLowerCase();
                    return type === "text" || type === "search" || type === "url" || type === "tel" || type === "email";
                }
                return false;
            });

            if (firstText instanceof HTMLElement) {
                firstText.focus();
                selectAllText(firstText);
            }
        },
        clear(): void {
            snapshot = {};
        },
        detach(): void {
            listenerHandle.detach();
        },
    };
}
